import React, { useEffect } from 'react';
import { Target, Eye, Heart, Users, Award, Shield } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import Counter from '../components/Counter';
import SEO from '../components/SEO';
import ResponsiveCardSlider from '../components/ResponsiveCardSlider';

const About = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen">
      <SEO 
        title="About Us - Leading Trademark Protection Experts"
        description="Learn about Trademark Resolutions' 15+ years of trademark protection experience. 98% success rate, 50+ expert team members, and 10,000+ businesses protected."
        keywords="trademark attorneys, IP protection experts, trademark professionals, brand protection services, trademark law firm"
      />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1521737711867-e3b97375f902?q=80&w=2070)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            About Trademark Resolutions
          </h1>
          <p className="text-xl sm:text-2xl text-gray-200 max-w-3xl mx-auto">
            Your trusted partner in trademark protection and intellectual property services
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ResponsiveCardSlider className="grid md:grid-cols-3 gap-8">
            <Card className="p-8 text-center hover:shadow-xl transition-all duration-300 border-2 border-gray-200 hover:border-teal-400">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Target className="h-8 w-8 text-teal-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Our Mission</h3>
              <p className="text-gray-600 leading-relaxed">
                To provide accessible, expert trademark services that empower businesses to protect their most valuable asset—their brand identity.
              </p>
            </Card>

            <Card className="p-8 text-center hover:shadow-xl transition-all duration-300 border-2 border-gray-200 hover:border-teal-400">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Eye className="h-8 w-8 text-teal-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Our Vision</h3>
              <p className="text-gray-600 leading-relaxed">
                To be the most trusted name in trademark protection, setting the standard for service excellence and client success in intellectual property.
              </p>
            </Card>

            <Card className="p-8 text-center hover:shadow-xl transition-all duration-300 border-2 border-gray-200 hover:border-teal-400">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart className="h-8 w-8 text-teal-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Our Values</h3>
              <p className="text-gray-600 leading-relaxed">
                Integrity, expertise, and client success guide everything we do. We're committed to providing exceptional service and protecting your brand.
              </p>
            </Card>
          </ResponsiveCardSlider>
        </div>
      </section>

      {/* Who We Are - With Image */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="order-2 lg:order-1 flex items-start">
              <img
                src="https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw1fHx0ZWFtd29ya3xlbnwwfHx8fDE3NjM1MDM0NjR8MA&ixlib=rb-4.1.0&q=85"
                alt="Our professional trademark team"
                className="rounded-xl shadow-2xl w-full h-auto max-h-[400px] object-cover"
              />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Who We Are
              </h2>
              <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
                <p>
                  Trademark Resolutions is a dedicated trademark services provider specializing in helping businesses protect their most valuable asset: their brand. We combine legal expertise with modern technology to deliver efficient, reliable trademark registration and protection services.
                </p>
                <p>
                  Our team consists of experienced trademark professionals, paralegals, and customer service specialists who are passionate about helping businesses succeed. We understand that your brand represents years of hard work, innovation, and dedication—and we're committed to safeguarding it.
                </p>
                <div className="grid grid-cols-2 gap-4 mt-6">
                  <div className="bg-teal-50 p-4 rounded-lg">
                    <p className="text-3xl font-bold text-teal-600 mb-1"><Counter end={15} suffix="+" /></p>
                    <p className="text-sm text-gray-600">Years Experience</p>
                  </div>
                  <div className="bg-teal-50 p-4 rounded-lg">
                    <p className="text-3xl font-bold text-teal-600 mb-1"><Counter end={50} suffix="+" /></p>
                    <p className="text-sm text-gray-600">Expert Team Members</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Sets Us Apart */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              What Sets Us Apart
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Discover why businesses choose us for their intellectual property needs
            </p>
          </div>

          <ResponsiveCardSlider className="grid md:grid-cols-3 gap-8">
            <Card className="p-8 text-center hover:shadow-xl transition-all duration-300 border-2 border-gray-200 hover:border-teal-400">
              <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">Proven Track Record</h3>
              <p className="text-gray-600 leading-relaxed">
                With over 15 years of experience and thousands of successful registrations, we have the expertise to handle even the most complex cases with confidence.
              </p>
            </Card>

            <Card className="p-8 text-center hover:shadow-xl transition-all duration-300 border-2 border-gray-200 hover:border-teal-400">
              <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">Dedicated Support</h3>
              <p className="text-gray-600 leading-relaxed">
                Each client is assigned a dedicated case manager who provides personalized attention and regular updates throughout the entire process.
              </p>
            </Card>

            <Card className="p-8 text-center hover:shadow-xl transition-all duration-300 border-2 border-gray-200 hover:border-teal-400">
              <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">Comprehensive Protection</h3>
              <p className="text-gray-600 leading-relaxed">
                From initial search to ongoing monitoring and enforcement, we provide complete trademark protection services to keep your brand secure.
              </p>
            </Card>
          </ResponsiveCardSlider>
        </div>
      </section>

      {/* Our Commitment */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Our Commitment to Excellence
              </h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Quality Assurance</h3>
                    <p className="text-gray-600">
                      Every application undergoes rigorous quality checks to ensure accuracy and completeness before submission to the USPTO.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Transparent Communication</h3>
                    <p className="text-gray-600">
                      We believe in keeping you informed at every step. You'll receive regular updates and have direct access to your case manager.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Long-Term Partnership</h3>
                    <p className="text-gray-600">
                      Our relationship doesn't end at registration. We provide ongoing support, monitoring, and renewal services for the life of your trademark.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=2070"
                alt="Team collaboration and success"
                className="rounded-xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-teal-600 text-white p-6 rounded-lg shadow-xl">
                <p className="text-4xl font-bold mb-1"><Counter end={98} suffix="%" /></p>
                <p className="text-sm">Success Rate</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?q=80&w=2070"
            alt="Professional success"
            className="w-full h-full object-cover"
          />
          {/* Color Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-teal-800/90 to-slate-900/95"></div>
        </div>

        {/* Content */}
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-6 py-2 mb-6">
            <span className="text-white text-sm font-semibold">✓ Your Success Is Our Priority</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6 leading-tight">
            Let's Protect Your Brand Together
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-3xl mx-auto leading-relaxed">
            Partner with a team that truly cares about your success. Contact us today to discuss how we can help protect and grow your brand with our comprehensive trademark services.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => window.location.href = '/contact'}
              size="lg"
              className="bg-teal-600 hover:bg-teal-700 text-white px-10 py-7 text-lg font-bold shadow-2xl hover:shadow-3xl hover:scale-105 transition-all duration-300"
            >
              Schedule a Consultation →
            </Button>
            <Button
              onClick={() => window.location.href = '/'}
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-slate-900 px-10 py-7 text-lg font-bold transition-all duration-300"
            >
              Explore Our Services
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;